import { AxiosError } from 'axios'
import {
  EnrichedGeckoInspection,
  InboxFilterState,
  InboxSortDirection,
} from 'features/inbox/types/types'
import { computeInspectionStatus } from 'features/inbox/utils/computeInspectionStatus'
import { getWorstDraFromIndications } from 'features/inbox/utils/draRating'
import {
  IndicationItem,
  IndicationSet,
  IndicationSetType,
} from 'features/indicationTriage'
import {
  computeScanArea,
  computeScanAreaRatio,
  computeTotalComponentArea,
} from 'features/scans'
import groupBy from 'lodash/groupBy'
import keyBy from 'lodash/keyBy'
import { useMemo } from 'react'
import {
  GeckoAssetModel,
  GeckoComponent,
  GeckoComponentDataset,
  GeckoComponentModel,
  GeckoEntry,
  GeckoJob,
  GeckoMarkupLayer,
  GeckoMeasurement,
  GeckoMeasurementUnit,
  GeckoUnit,
} from 'types'

import { useFilteredInspections } from './useFilteredInspection'
import { useInboxRolodexEntries } from './useInboxRolodexEntries'

const DEFAULT_INBOX_FILTER_STATE: InboxFilterState = {
  statusFilters: [],
  inspectionTypeFilters: [],
  draLevelFilters: [],
  searchText: '',
  showFutureInspections: false,
}

interface UseInspectionDataParams {
  siteId?: string
  orgId?: string
  sortDirection?: InboxSortDirection
  filters?: InboxFilterState
}

export interface InspectionRelatedData {
  componentDatasets: ReadonlyArray<GeckoComponentDataset>
  markupLayers: ReadonlyArray<GeckoMarkupLayer>
  unit: GeckoUnit | undefined
  component: GeckoComponent | undefined
  job: GeckoJob | undefined
  assetModel: GeckoAssetModel | undefined
  componentModel: GeckoComponentModel | undefined
}

interface UseInspectionDataResult {
  inspections: ReadonlyArray<EnrichedGeckoInspection>
  inspectionData: Record<string, InspectionRelatedData>
  isLoading: boolean
  error?: AxiosError
  totalEntries: number
}

const DEFAULT_SCAN_AREA_DATA = {
  scanArea: GeckoMeasurement.CreateFromValue(
    0,
    GeckoMeasurementUnit.SquareMeters
  ),
  maximumPossibleScanArea: GeckoMeasurement.CreateFromValue(
    0,
    GeckoMeasurementUnit.SquareMeters
  ),
  scanAreaRatio: 0,
}

/**
 * Orchestration hook that loads and enriches inbox inspection data
 * Uses useInboxRolodexEntries for efficient data loading, then enriches inspections
 * with derived status, scan area, and other computed fields
 *
 * Unfortunately, because many of the fields we use for filters are derived, all inspections
 * and much of their metadata for the org/site need to be loaded - pagination isn't viable.
 *
 */
export const useInspectionData = (
  params: UseInspectionDataParams = {}
): UseInspectionDataResult => {
  const { siteId, orgId, sortDirection = 'newest', filters } = params

  // Load all inbox data with just 2 efficient queries
  const {
    inspections,
    jobs,
    componentDatasets,
    units,
    components,
    markupLayers,
    assetModels,
    indications,
    indicationSets,
    isLoading,
    error,
  } = useInboxRolodexEntries({
    siteId,
    orgId,
    sortDirection,
  })

  const inspectionData = useMemo(() => {
    const componentDatasetsByInspection = groupBy(
      componentDatasets,
      (cds) => cds.tags.inspection
    )
    const markupLayersByInspection = groupBy(
      markupLayers,
      (layer) => layer.tags.inspection
    )

    const unitsById = keyBy(units, 'id')
    const componentsById = keyBy(components, 'id')
    const jobsById = keyBy(jobs, 'id')
    const assetModelsByUnitId = keyBy(
      assetModels,
      (assetModel) => assetModel.tags.unit
    )

    const result: Record<string, InspectionRelatedData> = {}

    inspections.forEach((inspection) => {
      const unit = inspection.tags?.unit
        ? unitsById[inspection.tags.unit]
        : undefined

      const component = inspection.tags?.component
        ? componentsById[inspection.tags.component]
        : undefined

      const job = inspection.tags?.job
        ? jobsById[inspection.tags.job]
        : undefined

      const unitId = inspection.tags?.unit
      const assetModel = unitId ? assetModelsByUnitId[unitId] : undefined

      const componentModel =
        component?.id && assetModel?.data?.components
          ? assetModel.data.components.get(component.id)
          : undefined

      result[inspection.id] = {
        componentDatasets: componentDatasetsByInspection[inspection.id] ?? [],
        markupLayers: markupLayersByInspection[inspection.id] ?? [],
        unit,
        component,
        job,
        assetModel,
        componentModel,
      }
    })

    return result
  }, [
    inspections,
    componentDatasets,
    markupLayers,
    units,
    components,
    jobs,
    assetModels,
  ])

  // Create Map versions for scan area calculations
  const markupLayersByInspectionMap = useMemo(
    () =>
      new Map(
        Object.entries(inspectionData).map(([id, data]) => [
          id,
          data.markupLayers,
        ])
      ),
    [inspectionData]
  )

  const componentModelsByInspectionMap = useMemo(() => {
    const map = new Map<string, GeckoComponentModel>()
    Object.entries(inspectionData).forEach(([id, data]) => {
      if (data.componentModel) {
        map.set(id, data.componentModel)
      }
    })
    return map
  }, [inspectionData])

  const scanAreaCache = useMemo(() => {
    const cache = new Map<
      string,
      {
        scanArea: GeckoMeasurement
        maximumPossibleScanArea: GeckoMeasurement
        scanAreaRatio: number
      }
    >()

    inspections.forEach((inspection) => {
      const componentModel = componentModelsByInspectionMap.get(inspection.id)
      const individualComponentModelsForInspection: ReadonlyMap<
        string,
        GeckoComponentModel
      > = componentModel
        ? new Map([[inspection.id, componentModel]])
        : new Map()

      const individualMarkupLayersForInspection = new Map([
        [inspection.id, markupLayersByInspectionMap.get(inspection.id) ?? []],
      ])

      const scanArea = computeScanArea(
        individualMarkupLayersForInspection,
        individualComponentModelsForInspection
      )

      const maximumPossibleScanArea = computeTotalComponentArea(
        individualComponentModelsForInspection
      )

      const scanAreaRatio = computeScanAreaRatio(
        individualMarkupLayersForInspection,
        individualComponentModelsForInspection
      )

      cache.set(inspection.id, {
        scanArea,
        maximumPossibleScanArea,
        scanAreaRatio,
      })
    })

    return cache
  }, [inspections, componentModelsByInspectionMap, markupLayersByInspectionMap])

  const indicationMetricsByInspection = useMemo(() => {
    const result = new Map<
      string,
      {
        indicationCount: number
        indicationsByType: Record<
          IndicationSetType,
          Array<{ indicationSet: IndicationSet; indications: IndicationItem[] }>
        >
        worstDRA?: number
      }
    >()

    const indicationsByInspectionId = groupBy(
      indications,
      (indication) => indication.tags.inspection
    )

    const indicationSetsById = keyBy(indicationSets, 'id')

    inspections.forEach((inspection) => {
      const inspectionIndications =
        indicationsByInspectionId[inspection.id] ?? []
      const indicationCount = inspectionIndications.length

      const byType: Record<
        IndicationSetType,
        Array<{ indicationSet: IndicationSet; indications: IndicationItem[] }>
      > = {
        raut_report: [],
        weld_report: [],
        trilat_report: [],
        damage_annotation: [],
        emat_report: [],
      }

      const indicationsBySetId = groupBy(
        inspectionIndications,
        (indication) => indication.indicationSetId
      )

      Object.entries(indicationsBySetId).forEach(([setId, setIndications]) => {
        const indicationSet = indicationSetsById[setId]
        if (indicationSet && indicationSet.indicationSetType) {
          const type = indicationSet.indicationSetType
          if (!byType[type]) {
            byType[type] = []
          }
          byType[type].push({
            indicationSet,
            indications: setIndications as IndicationItem[],
          })
        }
      })

      const rautReportIndications = byType.raut_report.flatMap(
        (group) => group.indications
      )
      const worstDRA = getWorstDraFromIndications(rautReportIndications)

      result.set(inspection.id, {
        indicationCount,
        indicationsByType: byType,
        worstDRA,
      })
    })

    return result
  }, [inspections, indications, indicationSets])

  // Enrich with derived status and any other relevant fields.
  const enrichedInspections: ReadonlyArray<EnrichedGeckoInspection> = useMemo(
    () =>
      inspections.map((inspection) => {
        const data = inspectionData[inspection.id]

        /////// Status ///////
        const status = computeInspectionStatus(
          inspection,
          data.componentDatasets as GeckoComponentDataset[]
        )

        /////// Scan area (from cache) ///////
        const scanAreaData =
          scanAreaCache.get(inspection.id) ?? DEFAULT_SCAN_AREA_DATA

        /////// Percentage progress ///////
        let percentageProgress: number
        if (
          status === 'completed' ||
          status === 'preliminary_results_available' ||
          status === 'closed'
        ) {
          percentageProgress = 100
        } else if (status === 'scheduled') {
          percentageProgress = 0
        } else {
          percentageProgress = Math.min(100, 100 * scanAreaData.scanAreaRatio)
        }

        /////// Indication metrics ///////
        const indicationMetrics = indicationMetricsByInspection.get(
          inspection.id
        ) ?? {
          indicationCount: 0,
          indicationsByType: {
            raut_report: [],
            weld_report: [],
            trilat_report: [],
            damage_annotation: [],
            emat_report: [],
          },
          worstDRA: undefined,
        }

        return {
          ...inspection,
          data: {
            ...inspection.data,
            derivedStatus: status,
            unitName: data.unit?.data.name,
            componentName: data.component?.data.name,
            jobName: data.job?.data.name,
            percentageProgress: percentageProgress,
            scanArea: scanAreaData.scanArea,
            maximumPossibleScanArea: scanAreaData.maximumPossibleScanArea,
            indicationCount: indicationMetrics.indicationCount,
            indicationsByType: indicationMetrics.indicationsByType,
            worstDRA: indicationMetrics.worstDRA,
          },
        }
      }),
    [inspections, inspectionData, scanAreaCache, indicationMetricsByInspection]
  )

  const { inspections: filteredInspections } = useFilteredInspections({
    inspections: enrichedInspections as ReadonlyArray<GeckoEntry>,
    filters: filters ?? DEFAULT_INBOX_FILTER_STATE,
  })

  return {
    inspections: filteredInspections as ReadonlyArray<EnrichedGeckoInspection>,
    inspectionData,
    isLoading,
    error,
    totalEntries: inspections.length,
  }
}
